import React from "react";
import * as O from "fp-ts/Option";
import * as RA from "fp-ts/ReadonlyArray";
import { useMutation, useQuery } from "@apollo/client";
import { ApolloError, gql } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { pipe } from "fp-ts/function";

import { Button } from "src/components/button/button";
import { Error } from "src/components/error/error";
import { FormikSelect } from "src/components/select/select";
import { SectionTitle } from "src/components/typography/typography";
import {
    CallCloudCodeInput,
    CallCloudCodePayload,
    CloudCodeFunction,
    SchoolConnection,
    SchoolEdge,
} from "src/generated/types";
import { parseError } from "src/utils/parse_error";

import { AdminLayout } from "./admin_layout";
import { getReportTypes, getReportYears, getSchoolChoices } from "./helpers";
import { ALL_SCHOOLS_QUERY } from "./queries";

/*
 * GraphQL
 */

/*
 * We use `callCloudCode` instead of `createReport` b/c the BE needs to be able to handle year
 * validation, create relations with the school, and create URLs. The result `report` can be `null`!
 *
 * Hans H. 18 May 2020
 */
const CREATE_REPORTS_MUTATION = gql`
    mutation CreateReports($input: CallCloudCodeInput!) {
        callCloudCode(input: $input) {
            result
        }
    }
`;

/*
 * Types
 */

interface ApolloMutationVars {
    input: CallCloudCodeInput;
}

interface ApolloMutationData {
    callCloudCode: CallCloudCodePayload;
}

interface ApolloQueryData {
    schools: SchoolConnection;
}

interface FormValues {
    schoolId: string;
    year: string;
    type: string;
}

/*
 * Main
 */

export const CreateReportPage = (): React.ReactElement => {
    const { data, error } = useQuery<ApolloQueryData, Empty>(ALL_SCHOOLS_QUERY, {
        fetchPolicy: "network-only",
    });
    const [createReports, { loading }] = useMutation<ApolloMutationData, ApolloMutationVars>(CREATE_REPORTS_MUTATION);

    const initialValues: FormValues = {
        schoolId: "",
        year: "",
        type: "",
    };

    if (error) return <Error />;

    return (
        <AdminLayout>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, { resetForm, setSubmitting }): void => {
                    createReports({
                        variables: {
                            input: {
                                functionName: CloudCodeFunction.CreateReports,
                                params: {
                                    schoolId: values.schoolId,
                                    year: values.year,
                                    type: values.type,
                                },
                            },
                        },
                    })
                        .then(() => {
                            toast.success("Report(s) created successfully");
                        })
                        .catch((e: ApolloError) => {
                            toast.error(parseError(e));
                        });

                    setSubmitting(false);
                    resetForm();
                }}
                validationSchema={Yup.object().shape({
                    schoolId: Yup.string().required("Required"),
                    year: Yup.string().required("Required"),
                    type: Yup.string().required("Required"),
                })}
            >
                {(formik: FormikProps<FormValues>) => (
                    <Form>
                        <SectionTitle>Create Report</SectionTitle>
                        <FormikSelect
                            label="School"
                            name="schoolId"
                            options={pipe(
                                O.fromNullable(data?.schools.edges),
                                O.fold(
                                    () => RA.empty,
                                    (edges) => getSchoolChoices(edges as ReadonlyArray<SchoolEdge>),
                                ),
                            )}
                        />
                        <FormikSelect label="Year" name="year" options={getReportYears()} />
                        <FormikSelect label="Type" name="type" options={getReportTypes()} />
                        <Button disabled={formik.isSubmitting || !formik.isValid || loading} type="submit">
                            Submit
                        </Button>
                    </Form>
                )}
            </Formik>
        </AdminLayout>
    );
};
